import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useReveal } from "../../hooks/useReveal";
import { easing } from "../../lib/animations";
import { MagneticBar } from "./MagneticBar";
import { ScrambleTextControlled } from "./ScrambleText";

const NAV_LINKS = [
    { label: "LOOKBOOK", to: "/shop" },
    { label: "MANIFEST", to: "/about" },
    { label: "CART", to: "/cart" },
    { label: "MY ACCOUNT", to: "/account" },
];

const SIGNAL_LINKS = [
    {
        title: "Channels",
        items: [
            {
                label: "Instagram",
                href: "https://www.instagram.com/proof.wear/",
                external: true,
            },
            {
                label: "Facebook",
                href: "https://www.facebook.com/profile.php?id=100077555107752",
                external: true,
            },
            {
                label: "Pinterest",
                href: "https://pl.pinterest.com/proofwear/",
                external: true,
            },
        ],
    },
];

const ExternalArrow = () => (
    <svg width="10" height="10" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <path d="M3 11L11 3" stroke="currentColor" />
        <path d="M4 3H11V10" stroke="currentColor" />
    </svg>
);

const slug = (s) => s.toLowerCase().replace(/[^\w]+/g, "-").replace(/^-|-$/g, "");

const NavLink = ({ label, to, external }) => {
    const [hover, setHover] = useState(false);
    return (
        <Link
            to={to}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                color: "#fff",
                textDecoration: "none",
                fontFamily: "Manrope, sans-serif",
                fontWeight: 500,
                fontSize: "clamp(13px, 1.05vw, 18px)",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                whiteSpace: "nowrap",
                padding: "8px 0",
            }}
            data-testid={`footer-nav-${slug(label)}`}
        >
            <ScrambleTextControlled text={label} active={hover} duration={420} stagger={20} />
            {external && <ExternalArrow />}
        </Link>
    );
};

const SignalLink = ({ label, href, to, external }) => {
    const [hover, setHover] = useState(false);
    const baseStyle = {
        color: "rgba(255,255,255,0.85)",
        textDecoration: "none",
        fontFamily: "Manrope, sans-serif",
        fontSize: 13,
        letterSpacing: "0.04em",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 0",
    };
    const inner = (
        <>
            <ScrambleTextControlled text={label} active={hover} duration={420} stagger={18} />
            {external && <ExternalArrow />}
        </>
    );
    if (to) {
        return (
            <Link
                to={to}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                style={baseStyle}
                data-testid={`footer-social-${slug(label)}`}
            >
                {inner}
            </Link>
        );
    }
    return (
        <a
            href={href}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={baseStyle}
            data-testid={`footer-social-${slug(label)}`}
        >
            {inner}
        </a>
    );
};

const FooterContactCard = ({ inView }) => {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [submitted, setSubmitted] = useState(false);

    const onSubmit = (e) => {
        e.preventDefault();
        if (email.length > 3 && email.includes("@")) {
            setSubmitted(true);
            setEmail("");
            setTimeout(() => {
                setSubmitted(false);
                setOpen(false);
            }, 4000);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.9, ease: easing.primary, delay: 0.15 }}
            data-testid="footer-contact-card"
            style={{
                background: "#ecebe5",
                color: "#0c0c0c",
                padding: "34px 30px 28px",
                borderRadius: 0,
                position: "relative",
            }}
        >
            {/* DESKTOP lockup — sygnet video + horizontal logo */}
            <div
                className="proof-footer-lockup-desktop"
                style={{
                    marginBottom: 22,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0,
                    lineHeight: 0,
                }}
                data-testid="footer-brand-lockup"
            >
                <video
                    autoPlay
                    muted
                    playsInline
                    loop
                    preload="auto"
                    data-testid="footer-brand-video"
                    style={{
                        height: 62,
                        width: "auto",
                        display: "block",
                        objectFit: "contain",
                        userSelect: "none",
                    }}
                >
                    <source src="/assets/yk/proof-sygnet-black.webm" type="video/webm" />
                </video>
                <img
                    src="/assets/yk/proof-black-horizontal.png"
                    alt="PROOF"
                    draggable={false}
                    style={{
                        height: 46,
                        width: "auto",
                        display: "block",
                        filter: "invert(1)",
                        userSelect: "none",
                    }}
                />
            </div>

            {/* MOBILE lockup — full animated logo video */}
            <div
                className="proof-footer-lockup-mobile"
                style={{
                    marginBottom: 22,
                    display: "none",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    lineHeight: 0,
                }}
                data-testid="footer-brand-lockup-mobile"
            >
                <video
                    autoPlay
                    muted
                    playsInline
                    loop
                    preload="auto"
                    data-testid="footer-brand-video-mobile"
                    style={{
                        height: 110,
                        width: "auto",
                        maxWidth: "70%",
                        display: "block",
                        objectFit: "contain",
                        userSelect: "none",
                    }}
                >
                    <source src="/assets/yk/proof-logo-animated-black.webm" type="video/webm" />
                </video>
            </div>
            <p
                style={{
                    margin: 0,
                    fontFamily: "'EB Garamond', serif",
                    fontStyle: "italic",
                    fontSize: 15,
                    lineHeight: 1.55,
                    color: "#3a3a3a",
                    marginBottom: 26,
                    maxWidth: "32ch",
                }}
            >
                Quiet-luxury streetwear, made slowly in Poland.
                Heavy cotton, honest construction, built to outlive trends.
            </p>

            <AnimatePresence mode="wait" initial={false}>
                {!open && (
                    <motion.button
                        key="cta"
                        type="button"
                        onClick={() => setOpen(true)}
                        data-testid="footer-contact-cta"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{
                            background: "transparent",
                            border: "none",
                            color: "#0c0c0c",
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 11,
                            letterSpacing: "0.28em",
                            textTransform: "uppercase",
                            cursor: "pointer",
                            padding: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 12,
                        }}
                    >
                        Get in touch
                        <svg width="22" height="9" viewBox="0 0 22 9" fill="none" aria-hidden="true">
                            <line x1="0" y1="4.5" x2="20" y2="4.5" stroke="#0c0c0c" />
                            <path d="M16 1L20 4.5L16 8" stroke="#0c0c0c" fill="none" />
                        </svg>
                    </motion.button>
                )}

                {open && !submitted && (
                    <motion.form
                        key="form"
                        onSubmit={onSubmit}
                        data-testid="footer-contact-form"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.45, ease: easing.primary }}
                        style={{
                            display: "flex",
                            alignItems: "stretch",
                            borderBottom: "1px solid rgba(12,12,12,0.45)",
                            paddingBottom: 4,
                            gap: 14,
                        }}
                    >
                        <input
                            autoFocus
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Your email"
                            data-testid="footer-contact-email"
                            style={{
                                flex: 1,
                                background: "transparent",
                                border: "none",
                                outline: "none",
                                color: "#0c0c0c",
                                fontFamily: "'Space Grotesk', Manrope, sans-serif",
                                fontSize: 15,
                                padding: "10px 0",
                                letterSpacing: "0.01em",
                            }}
                        />
                        <button
                            type="submit"
                            data-testid="footer-contact-submit"
                            style={{
                                background: "transparent",
                                border: "none",
                                color: "#0c0c0c",
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 11,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                                cursor: "pointer",
                                padding: "10px 0",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 10,
                            }}
                        >
                            Send
                            <svg width="22" height="9" viewBox="0 0 22 9" fill="none" aria-hidden="true">
                                <line x1="0" y1="4.5" x2="20" y2="4.5" stroke="#0c0c0c" />
                                <path d="M16 1L20 4.5L16 8" stroke="#0c0c0c" fill="none" />
                            </svg>
                        </button>
                    </motion.form>
                )}

                {open && submitted && (
                    <motion.div
                        key="ok"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.45, ease: easing.primary }}
                        data-testid="footer-contact-success"
                        style={{
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 11,
                            letterSpacing: "0.24em",
                            textTransform: "uppercase",
                            color: "#0c0c0c",
                            paddingBottom: 6,
                            borderBottom: "1px solid rgba(12,12,12,0.45)",
                        }}
                    >
                        Signal received. We will be in touch.
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export const Footer = () => {
    const [ref, inView] = useReveal({ threshold: 0.12 });
    const backToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

    return (
        <footer
            ref={ref}
            id="contact"
            data-testid="site-footer"
            style={{
                background: "#0c0c0c",
                color: "#fff",
                position: "relative",
                overflow: "hidden",
                padding: "clamp(36px, 4vw, 56px) clamp(28px, 4vw, 56px) 18px",
            }}
        >
            {/* Very dim, abstract background — concrete / shadow texture */}
            <div
                aria-hidden="true"
                style={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage:
                        "url('https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=1800&q=70')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    opacity: 0.14,
                    filter: "grayscale(1) blur(2px)",
                    zIndex: 0,
                }}
            />
            <div
                aria-hidden="true"
                style={{
                    position: "absolute",
                    inset: 0,
                    background:
                        "linear-gradient(180deg, rgba(12,12,12,0.6) 0%, rgba(12,12,12,0.92) 60%, rgba(12,12,12,1) 100%)",
                    zIndex: 0,
                }}
            />

            <div style={{ position: "relative", zIndex: 2 }}>
                {/* Top row — quiet marker */}
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
                    transition={{ duration: 0.7, ease: easing.primary }}
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontFamily: "JetBrains Mono, monospace",
                        fontSize: 11,
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.65)",
                        marginBottom: "clamp(28px, 3.5vw, 56px)",
                    }}
                >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
                        <motion.span
                            aria-hidden="true"
                            animate={{ opacity: [1, 0.2, 1] }}
                            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                            style={{
                                width: 6,
                                height: 6,
                                background: "#c1121f",
                                display: "inline-block",
                                boxShadow: "0 0 4px rgba(193, 18, 31, 0.55)",
                            }}
                        />
                        End of signal
                        <span style={{ width: 80, height: 1, background: "rgba(255,255,255,0.22)", marginLeft: 8 }} />
                    </span>
                    <button
                        onClick={backToTop}
                        data-testid="footer-back-to-top"
                        style={{
                            background: "transparent",
                            border: "none",
                            color: "inherit",
                            font: "inherit",
                            letterSpacing: "inherit",
                            textTransform: "inherit",
                            cursor: "pointer",
                            padding: 0,
                        }}
                    >
                        [Back to Top]
                    </button>
                </motion.div>

                {/* MAIN GRID: left brand card | right nav + signal columns */}
                <div
                    className="proof-footer-grid"
                    style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(280px, 380px) 1fr",
                        gap: "clamp(40px, 5vw, 96px)",
                        alignItems: "start",
                    }}
                >
                    {/* LEFT — Brand statement card */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
                        <FooterContactCard inView={inView} />

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                            transition={{ duration: 0.9, ease: easing.primary, delay: 0.22 }}
                        >
                            <Link
                                to="/shipping"
                                data-testid="footer-stamp-card"
                                aria-label="Read our shipping policy"
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 6,
                                    background: "rgba(255,255,255,0.04)",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                    padding: "18px 22px",
                                    borderRadius: 0,
                                    textDecoration: "none",
                                    color: "inherit",
                                    transition: "background 0.3s ease, border-color 0.3s ease",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = "rgba(255,255,255,0.07)";
                                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                                }}
                            >
                                <span
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "baseline",
                                        flexWrap: "wrap",
                                        gap: 12,
                                        fontFamily: "'Space Grotesk', Manrope, sans-serif",
                                        fontWeight: 600,
                                        fontSize: 18,
                                        letterSpacing: "0.05em",
                                        textTransform: "uppercase",
                                        color: "#fff",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    <span
                                        style={{
                                            fontFamily: "JetBrains Mono, monospace",
                                            fontSize: 10,
                                            letterSpacing: "0.28em",
                                            textTransform: "uppercase",
                                            color: "rgba(255,255,255,0.5)",
                                            fontWeight: 400,
                                        }}
                                    >
                                        Made in
                                    </span>
                                    Poland.
                                </span>
                                <span
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 8,
                                        fontFamily: "'EB Garamond', serif",
                                        fontStyle: "italic",
                                        fontSize: 13,
                                        color: "rgba(255,255,255,0.55)",
                                        lineHeight: 1.5,
                                    }}
                                >
                                    Learn More about our shipping policy and pricing.
                                    <svg width="14" height="10" viewBox="0 0 22 9" fill="none" aria-hidden="true">
                                        <line x1="0" y1="4.5" x2="20" y2="4.5" stroke="currentColor" />
                                        <path d="M16 1L20 4.5L16 8" stroke="currentColor" fill="none" />
                                    </svg>
                                </span>
                            </Link>
                        </motion.div>
                    </div>

                    {/* RIGHT: nav single-line top + signal columns bottom */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 36 }}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                            transition={{ duration: 0.9, ease: easing.primary, delay: 0.25 }}
                            style={{
                                paddingTop: 12,
                                display: "flex",
                                flexDirection: "column",
                                gap: 16,
                            }}
                        >
                            <nav
                                aria-label="Footer"
                                style={{
                                    display: "flex",
                                    flexWrap: "nowrap",
                                    overflowX: "auto",
                                    justifyContent: "space-between",
                                    gap: "clamp(14px, 2vw, 36px)",
                                    paddingBottom: 10,
                                    minWidth: 0,
                                }}
                            >
                                {NAV_LINKS.map((l) => (
                                    <NavLink key={l.label} {...l} />
                                ))}
                            </nav>
                            <MagneticBar
                                barCount={140}
                                peakHeight={16}
                                baseHeight={3}
                                falloff={0.06}
                                color="rgba(255,255,255,0.55)"
                            />
                        </motion.div>

                        {/* Manifesto block — large quiet statement */}
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
                            transition={{ duration: 1.1, ease: easing.primary, delay: 0.35 }}
                            data-testid="footer-manifesto"
                            style={{
                                paddingTop: 8,
                            }}
                        >
                            <p
                                style={{
                                    margin: 0,
                                    fontFamily: "'Space Grotesk', Manrope, sans-serif",
                                    fontWeight: 500,
                                    fontSize: "clamp(28px, 4vw, 56px)",
                                    lineHeight: 1.15,
                                    letterSpacing: "-0.03em",
                                    color: "#fff",
                                    maxWidth: "32ch",
                                }}
                            >
                                Quality Survives Trends. Silence Is Luxury. Less Is More. Less Noise.
                            </p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                            transition={{ duration: 0.9, ease: easing.primary, delay: 0.42 }}
                            style={{
                                display: "grid",
                                gridTemplateColumns: "minmax(0, 1fr)",
                                gap: 28,
                                maxWidth: 360,
                            }}
                        >
                            {SIGNAL_LINKS.map((col) => (
                                <div
                                    key={col.title}
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 10,
                                        borderTop: "1px solid rgba(255,255,255,0.14)",
                                        paddingTop: 16,
                                    }}
                                >
                                    <h5
                                        style={{
                                            margin: 0,
                                            fontFamily: "JetBrains Mono, monospace",
                                            fontSize: 10,
                                            letterSpacing: "0.24em",
                                            textTransform: "uppercase",
                                            color: "rgba(255,255,255,0.45)",
                                            fontWeight: 400,
                                        }}
                                    >
                                        {col.title}:
                                    </h5>
                                    {col.items.map((it) => (
                                        <SignalLink key={it.label} {...it} />
                                    ))}
                                </div>
                            ))}
                        </motion.div>
                    </div>
                </div>

                <div
                    style={{
                        marginTop: "clamp(20px, 2.5vw, 40px)",
                        paddingTop: 18,
                        borderTop: "1px solid rgba(255,255,255,0.1)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 14,
                        fontFamily: "JetBrains Mono, monospace",
                        fontSize: 10,
                        letterSpacing: "0.24em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.4)",
                    }}
                >
                    <span>© {new Date().getFullYear()} Proof — Less noise. All rights reserved.</span>
                    <span style={{ display: "inline-flex", gap: 18, flexWrap: "wrap" }}>
                        <Link
                            to="/policy/privacy"
                            data-testid="footer-privacy"
                            style={{ color: "inherit", textDecoration: "none" }}
                        >
                            Privacy
                        </Link>
                        <Link
                            to="/policy/terms"
                            data-testid="footer-terms"
                            style={{ color: "inherit", textDecoration: "none" }}
                        >
                            Terms
                        </Link>
                        <Link
                            to="/policy/returns"
                            data-testid="footer-returns"
                            style={{ color: "inherit", textDecoration: "none" }}
                        >
                            Returns
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="footer-contact-link"
                            style={{ color: "inherit", textDecoration: "none" }}
                        >
                            Contact
                        </Link>
                        <span>Warsaw · Poland</span>
                    </span>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
